import api from './axios';
import { Expense, PaginatedResponse, UpdateExpenseData, TransactionType } from '../types/expense';

export interface ParsedExpenseData {
  date: string;
  paymentDate?: string;
  currency: string;
  name: string;
  amount: number;
  status: string;
  costCategory: string | null;
  contractor: string | null;
  originalText: string;
  transcription?: string;
  type?: TransactionType;
}

export interface CreateExpenseData {
  date: string;
  paymentDate?: string | null;
  currency: string;
  name: string;
  amount: number;
  originalText: string;
  status?: string;
  costCategory?: string | null;
  contractor?: string | null;
  type?: TransactionType;
}

export const expensesApi = {
  getAll: async (page: number = 1, limit: number = 20, type?: TransactionType, signal?: AbortSignal): Promise<PaginatedResponse<Expense>> => {
    const params: any = { page: page.toString(), limit: limit.toString() };
    if (type) params.type = type;
    const response = await api.get('/expenses', { params, signal });
    return response.data;
  },

  getOne: async (id: string): Promise<Expense> => {
    const response = await api.get(`/expenses/${id}`);
    return response.data;
  },

  update: async (id: string, data: UpdateExpenseData): Promise<Expense> => {
    const response = await api.patch(`/expenses/${id}`, data);
    return response.data;
  },

  confirm: async (id: string): Promise<Expense> => {
    const response = await api.post(`/expenses/${id}/confirm`);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/expenses/${id}`);
  },

  getCategories: async (): Promise<string[]> => {
    const response = await api.get('/expenses/categories');
    return response.data.categories;
  },

  // Preview methods (parse only, don't save)
  previewText: async (text: string): Promise<ParsedExpenseData> => {
    const response = await api.post('/expenses/preview', { text });
    return response.data;
  },

  previewAudio: async (formData: FormData): Promise<ParsedExpenseData> => {
    const response = await api.post('/expenses/preview-audio', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  previewInvoice: async (formData: FormData): Promise<ParsedExpenseData> => {
    const response = await api.post('/expenses/preview-invoice', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Create from validated data
  create: async (data: CreateExpenseData): Promise<Expense> => {
    const response = await api.post('/expenses/create', data);
    return response.data;
  },

  // Legacy methods (parse and save in one step)
  parseText: async (text: string): Promise<Expense> => {
    const response = await api.post('/expenses/parse', { text });
    return response.data;
  },

  parseAudio: async (formData: FormData): Promise<{ transcription: string; expense: Expense }> => {
    const response = await api.post('/expenses/parse-audio', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  parseInvoice: async (formData: FormData): Promise<Expense> => {
    const response = await api.post('/expenses/parse-invoice', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  generateDds: async (): Promise<{ documentUrl: string; created: boolean }> => {
    const response = await api.post('/expenses/generate-dds');
    return response.data;
  },

  getFinanceFolder: async (): Promise<{ folderUrl: string; created: boolean }> => {
    const response = await api.get('/expenses/finance-folder');
    return response.data;
  },

  createFinanceDocument: async (): Promise<{ spreadsheetUrl: string; spreadsheetId: string; folderId: string }> => {
    const response = await api.post('/expenses/create-finance-document');
    return response.data;
  },

  // Trash methods
  getTrashed: async (page: number = 1, limit: number = 20, signal?: AbortSignal): Promise<PaginatedResponse<Expense>> => {
    const response = await api.get('/expenses/trash', { params: { page, limit }, signal });
    return response.data;
  },

  restore: async (id: string): Promise<Expense> => {
    const response = await api.patch(`/expenses/trash/${id}/restore`);
    return response.data;
  },

  permanentDelete: async (id: string): Promise<void> => {
    await api.delete(`/expenses/trash/${id}`);
  },

  bulkRestore: async (ids: string[]): Promise<{ restored: number }> => {
    const response = await api.patch('/expenses/trash/bulk-restore', { ids });
    return response.data;
  },

  bulkPermanentDelete: async (ids: string[]): Promise<{ deleted: number }> => {
    const response = await api.delete('/expenses/trash/bulk-delete', { data: { ids } });
    return response.data;
  },

  emptyTrash: async (): Promise<{ deleted: number }> => {
    const response = await api.delete('/expenses/trash/empty');
    return response.data;
  },
};

export default expensesApi;
